/**
 * A learner's extra-time allowances in the person visit (S4, R1): the
 * registrar records an allowance its approver has already approved, corrects
 * it or revokes it, and every change is a revision kept with its actor, time
 * and reason. Only the registrar's seat opens this record; teaching seats read
 * a learner's effective percentage and minutes on their quiz runs, never the
 * approval behind them. The server decides every save: one record per day and
 * quiz, revision order, and what a revocation keeps.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, ListBox, Part, TaskBox } from "@/components/ui/box.schema";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import type { PeopleDetailResponse } from "@/lib/api/people";
import { academicRecordQuery } from "@/lib/api/students";
import {
  TIME_ALLOWANCES_KEY,
  timeAllowancesApi,
  timeAllowancesKey,
  type Allowance,
  type AllowanceRevision,
} from "@/lib/api/timeAllowances";
import type { Read } from "@/components/ui/box.schema";

import { personDisplayName } from "../person-record";
import { imam, queryRead, type PendingCommit, useTaskCommit } from "./commit";
import { resultState } from "./access-boxes";

import { allowanceCopy as copy, blankForm, formOf, recordWords, revisionWords, spanWords, termsOf, type Form } from "./time-allowance-copy";
import { AllowanceFields, type CourseChoice } from "./time-allowance-fields";


type Row = { kind: "record"; key: string; record: Allowance } | { kind: "revision"; key: string; revision: AllowanceRevision };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** One commit key makes one record: Check after a reload asks for the record that key made. */
const recordIdOf = (key: string) => (UUID.test(key) ? key : crypto.randomUUID());

interface ChangeDraft {
  allowance: string;
  mode: "correct" | "revoke";
  form: Form;
  reason: string;
}

export function useTimeAllowanceBoxes(personId: string | null, detailRead: Read<PeopleDetailResponse>): Box[] {
  const client = useQueryClient();
  const detail = detailRead.status === "ready" ? detailRead.value : detailRead.status === "stale" ? detailRead.last : null;
  const studentId = personId ? (detail?.student_record?.id ?? null) : null;
  const sid = studentId ?? "none";
  const book = useQuery({ queryKey: timeAllowancesKey(sid), queryFn: () => timeAllowancesApi.ofLearner(sid), enabled: Boolean(studentId), retry: false });
  const academic = useQuery({ ...academicRecordQuery(sid), enabled: Boolean(studentId), retry: false });
  const refresh = () => client.invalidateQueries({ queryKey: TIME_ALLOWANCES_KEY });

  const records = book.data?.allowances ?? [];
  const live = records.filter((record) => record.action !== "revoked");
  const subject = `person:${personId ?? "none"}`;

  const recordDraft = useTabDraft<Form, PendingCommit>({ box: "time-allowance-record", record: subject, version: null, initial: blankForm });
  const recordCommit = useTaskCommit({
    subject,
    words: { refused: copy.refused, unknown: copy.unknownRecord, unknownSentence: copy.unknownSentence, checkNote: copy.checkNote },
    refresh,
    keep: recordDraft,
  });

  const first = live[0];
  const changeDraft = useTabDraft<ChangeDraft, PendingCommit>({
    box: "time-allowance-change",
    record: subject,
    version: live.map((record) => `${record.id}:${record.revision}`).join(",") || null,
    initial: { allowance: first?.id ?? "", mode: "correct", form: first ? formOf(first) : blankForm, reason: "" },
  });
  const chosen = live.find((record) => record.id === changeDraft.value.allowance) ?? null;
  const changeCommit = useTaskCommit({
    subject: `allowance:${chosen?.id ?? "none"}`,
    words: { refused: copy.refused, unknown: copy.unknownChange, unknownSentence: copy.unknownSentence, checkNote: copy.checkNote },
    refresh,
    keep: changeDraft,
  });

  if (!personId || !studentId || !detail) return [];
  const at = (id: string) => `${id}-${personId}`;
  const name = personDisplayName(detail);
  const read = queryRead(book, copy.unavailable);

  // The learner's courses, and any a record already names, so a correction never drops one unseen.
  const courseChoices: CourseChoice[] = [];
  const seen = new Set<string>();
  const offer = (id: string, label: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    courseChoices.push({ id, label });
  };
  for (const year of academic.data?.years ?? [])
    for (const term of year.terms) for (const course of term.courses) offer(course.course_id, `${course.code} · ${course.title}`);
  for (const record of records) for (const course of record.courses) offer(course.id, course.code ? `${course.code} · ${course.title}` : course.title);

  /* -------- Extra time: every record and revision -------- */
  const rows = (value: { allowances: Allowance[] }): Row[] =>
    value.allowances.flatMap((record) => [
      { kind: "record" as const, key: record.id, record },
      ...record.history.map((revision) => ({ kind: "revision" as const, key: `${record.id}-${revision.revision}`, revision })),
    ]);
  const list = {
    id: at("extra-time"),
    kind: "list",
    job: copy.listJob,
    label: copy.listLabel,
    owner: imam,
    fold: "choice",
    act: { does: "open", verb: "Open" },
    read: read.status === "ready" && read.value.allowances.length === 0 ? { status: "empty", sentence: `No extra time is recorded for ${name}.` } : read,
    detail: () => (live.length ? live.map(recordWords).join(" · ") : copy.noneInForce),
    state: () => ({ class: "quiet", word: live.length ? `${live.length} in force` : copy.noneInForce }),
    rows,
    row: {
      key: (row: Row) => row.key,
      title: (row: Row) => (row.kind === "record" ? row.record.reference : copy.action[row.revision.action]),
      facts: (row: Row) => (row.kind === "record" ? recordWords(row.record) : revisionWords(row.revision)),
      state: (row: Row) =>
        row.kind === "record"
          ? row.record.action === "revoked" ? { class: "quiet", word: copy.revoked } : { class: "done", word: copy.inForce }
          : undefined,
    },
  } as unknown as ListBox<{ allowances: Allowance[] }, Row>;

  /* -------- Record an allowance -------- */
  const recordPart = at("time-allowance-new");
  const form = recordDraft.value;
  const setForm = (patch: Partial<Form>) => {
    recordCommit.reset();
    recordDraft.set((was) => ({ ...was, ...patch }));
  };
  const fresh = termsOf(form);
  const recordBox: TaskBox<unknown> = {
    id: at("time-allowance-record"),
    kind: "task",
    job: copy.recordJob,
    label: copy.recordLabel,
    owner: imam,
    fold: "choice",
    read,
    detail: () => copy.notApproval,
    state: () => resultState(recordCommit.result, recordDraft.kept ? { class: "draft", word: fresh.problems.length ? fresh.problems[0] : copy.readyToRecord } : { class: "quiet", word: live.length ? `${live.length} in force` : copy.noneInForce }),
    act: { does: "open", verb: "Record" },
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [{
      id: recordPart,
      label: copy.allowancePart,
      state: fresh.problems.length ? { class: "waiting", word: fresh.problems[0] } : { class: "draft", word: `${fresh.terms.percent}% · ${fresh.terms.all_quizzes ? copy.allScope : `${fresh.terms.course_ids.length} course${fresh.terms.course_ids.length === 1 ? "" : "s"}`}` },
      owner: imam,
      fold: "never",
      draft: "browser",
      keep: recordDraft,
      render: () => (
        <div className="grid gap-3">
          <p className="text-ui text-muted-foreground">{copy.recordNote}</p>
          <p className="text-ui text-muted-foreground">{copy.overlapNote}</p>
          <AllowanceFields id={recordPart} form={form} set={setForm} courses={courseChoices} disabled={recordCommit.pending} />
        </div>
      ),
    } as Part],
    commit: {
      verb: copy.recordVerb,
      cap: copy.recordCap,
      effects: () => [
        { id: "e1", sentence: fresh.problems.length ? `${name} gets an extra-time record.` : `${name}: ${fresh.terms.percent}% extra time · ${fresh.terms.all_quizzes ? copy.allScope : courseChoices.filter((course) => fresh.terms.course_ids.includes(course.id)).map((course) => course.label.split(" · ")[0]).join(", ")} · ${spanWords(fresh.terms.effective_from, fresh.terms.effective_until)} · approved by ${fresh.terms.approver}, ${fresh.terms.reference}.` },
        { id: "e2", sentence: copy.nextStart },
      ],
      blockers: (): Blocker[] => fresh.problems.map((sentence) => ({ kind: "missing", part: recordPart, sentence })),
      run: (key: string) =>
        recordCommit.run(key, {
          effects: [copy.nextStart],
          write: async (commitKey) => void (await timeAllowancesApi.save(studentId, recordIdOf(commitKey), { expected_revision: 0, action: "recorded", ...fresh.terms }, commitKey)),
        }),
    },
    result: recordCommit.result,
    draft: recordDraft.kept,
  } as unknown as TaskBox<unknown>;

  const boxes: Box[] = [list as unknown as Box, recordBox as unknown as Box];

  /* -------- Correct or revoke a record -------- */
  if (live.length) {
    const changePart = at("time-allowance-change");
    const change = changeDraft.value;
    const fix = change.mode === "correct";
    const setChange = (patch: Partial<ChangeDraft>) => {
      changeCommit.reset();
      changeDraft.set((was) => ({ ...was, ...patch }));
    };
    const corrected = termsOf(change.form);
    const reasonMissing = !change.reason.trim();
    const problems = [...(!chosen ? [copy.recordChoice] : []), ...(fix ? corrected.problems : []), ...(reasonMissing ? [fix ? copy.giveCorrectReason : copy.giveRevokeReason] : [])];
    const reference = chosen?.reference ?? "";
    boxes.push({
      id: at("time-allowance-change"),
      kind: "task",
      job: copy.changeJob,
      label: copy.changeLabel,
      owner: imam,
      fold: "choice",
      read,
      detail: () => (chosen ? `${chosen.reference}: ${recordWords(chosen)}` : copy.noneInForce),
      state: () => resultState(changeCommit.result, changeDraft.kept ? { class: "draft", word: problems.length ? problems[0] : copy.readyToChange } : { class: "quiet", word: `${live.length} in force` }),
      act: { does: "open", verb: "Correct or revoke" },
      candidate: { eligible: true, blocked: null, rank: null },
      parts: [{
        id: changePart,
        label: fix ? copy.correctIt : copy.revokeIt,
        state: problems.length ? { class: "waiting", word: problems[0] } : { class: "draft", word: copy.readyToChange },
        owner: imam,
        fold: "never",
        draft: "browser",
        keep: changeDraft,
        render: () => (
          <div className="grid gap-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor={`${changePart}-record`}>{copy.recordChoice}</Label>
                <NativeSelect
                  id={`${changePart}-record`}
                  value={change.allowance}
                  onChange={(event) => {
                    const next = live.find((record) => record.id === event.target.value);
                    if (next) setChange({ allowance: next.id, form: formOf(next) });
                  }}
                >
                  {live.map((record) => <option key={record.id} value={record.id}>{`${record.reference} · ${record.percent}%`}</option>)}
                </NativeSelect>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${changePart}-mode`}>{copy.changeChoice}</Label>
                <NativeSelect id={`${changePart}-mode`} value={change.mode} onChange={(event) => setChange({ mode: event.target.value as ChangeDraft["mode"] })}>
                  <option value="correct">{copy.correctIt}</option>
                  <option value="revoke">{copy.revokeIt}</option>
                </NativeSelect>
              </div>
            </div>
            {chosen && <p className="text-ui">{`In force: ${recordWords(chosen)}.`}</p>}
            {fix && <AllowanceFields id={changePart} form={change.form} set={(patch) => setChange({ form: { ...change.form, ...patch } })} courses={courseChoices} disabled={changeCommit.pending} />}
            <div className="grid gap-1.5">
              <Label htmlFor={`${changePart}-reason`}>{fix ? copy.correctReason : copy.revokeReason}</Label>
              <Textarea id={`${changePart}-reason`} rows={3} value={change.reason} onChange={(event) => setChange({ reason: event.target.value })} />
            </div>
          </div>
        ),
      } as Part],
      commit: {
        verb: fix ? `Correct ${reference}` : `Revoke ${reference}`,
        cap: fix ? copy.correctCap : copy.revokeCap,
        effects: () =>
          !chosen
            ? []
            : fix
              ? [
                  { id: "e1", sentence: `The record will read: ${corrected.terms.percent}% · ${corrected.terms.all_quizzes ? copy.allScope : courseChoices.filter((course) => corrected.terms.course_ids.includes(course.id)).map((course) => course.label.split(" · ")[0]).join(", ")} · ${spanWords(corrected.terms.effective_from || chosen.effective_from, corrected.terms.effective_until)}. What it reads now stays in its history.` },
                  { id: "e2", sentence: copy.keptRuns },
                ]
              : [{ id: "e1", sentence: `No later Start uses ${chosen.reference}. Its history stays, and a run already started keeps its time.` }],
        blockers: (): Blocker[] => problems.map((sentence) => ({ kind: "missing", part: changePart, sentence })),
        run: (key: string) =>
          changeCommit.run(key, {
            effects: fix ? [copy.keptRuns] : [`No later Start uses ${reference}.`],
            write: async (commitKey) => {
              if (!chosen) return;
              const reason = change.reason.trim();
              await timeAllowancesApi.save(
                studentId,
                chosen.id,
                fix
                  ? { expected_revision: chosen.revision, action: "corrected", reason, ...corrected.terms }
                  : { expected_revision: chosen.revision, action: "revoked", reason },
                commitKey,
              );
            },
          }),
      },
      result: changeCommit.result,
      draft: changeDraft.kept,
    } as unknown as Box);
  }
  return boxes;
}
